import React, { useEffect, useState } from 'react';
import './UsersDetailsGrid.css';
import BasicCli from '../../main/components/basicCli/BasicCli';
import eFormType from '../../main/assets/enums/eFormType';
import DeleteModal from '../../main/components/modal/delete/DeleteModal';
import { ApiConstants } from '../../main/constants/ApiConstants';
import IUser from '../../main/interfaces/IUser';
import UsersInventory from './userInventory/UserInventory';
import axiosInstance from '../../main/initializers/axios';
import IRole from '../../main/interfaces/IRole';
import EditModal from '../../main/components/modal/edit/EditModal';
import GeneralTable from '../../main/components/grid/GeneralTable';
import IPagination from '../../main/interfaces/IPagination';
import IGridItems from '../../main/interfaces/IGridItems';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { useTranslation } from 'react-i18next';
import GeneralModal from '../../main/components/modal/general/GeneralModal';
import { useCan } from '../../core/authorization/PermissionProvider';

const UsersDetailsGrid = () => {
  const canCreate = useCan('platform.memberships', 'create');
  const canUpdate = useCan('platform.memberships', 'update');
  const canDelete = useCan('platform.memberships', 'delete');
  const canExecute = useCan('platform.memberships', 'execute');
  const [users, setUsers] = useState<IUser[]>([]);
  const [roles, setRoles] = useState<IRole[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isModalEditOpen, setIsModalEditOpen] = useState<boolean>(false);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState<boolean>(false);
  const [approvedUser, setApprovedUser] = useState<boolean>(false);
  const [selectedVal, setSelectedVal] = useState<IUser | null>(null);
  const [isActivationModalOpen, setIsActivationModalOpen] = useState<boolean>(false);
  const [activationCode, setActivationCode] = useState<string>('');
  const [activationLoading, setActivationLoading] = useState<boolean>(false);

  const [gridItems, setGridItems] = useState<IGridItems[]>([]);
  const [pagination, setPagination] = useState<IPagination>({
    count: 1,
    page: 1,
    itemPerPage: 50,
    itemsPerPage: [10, 20, 50, 100],
  });

  const { i18n } = useTranslation();

  const getRoles = () => {
    axiosInstance.get(ApiConstants.Roles.list).then((res) => setRoles(res.data ?? []));
  };

  const getTable = (pag?: IPagination, gridOverride?: IGridItems[]) => {
    const effective = pag ?? pagination;
    const effectiveGrid = gridOverride ?? gridItems ?? [];

    axiosInstance.post(ApiConstants.User.listCt, { gridItems: effectiveGrid, pagination: effective }).then((res) => {
      const listTable = res.data?.items ?? [];
      const p = res.data?.pagination ?? {};
      const cols = res.data?.gridItems ?? [];

      setUsers(listTable);
      setGridItems(cols);

      setPagination((prev) => ({
        ...prev,
        count: p.count ?? prev.count,
        page: p.page ?? prev.page,
        itemPerPage: p.itemPerPage ?? prev.itemPerPage,
        itemsPerPage: p.itemsPerPage ?? prev.itemsPerPage,
      }));
    });
  };

  const selectedItemPagination = (pag: IPagination) => {
    setPagination(pag);
    getTable(pag);
  };

  const deleteDataSet = (id: number) => {
    if (!canDelete) return;
    axiosInstance.post(ApiConstants.User.delete, { Id: id }).then(() => {
      setIsModalDeleteOpen(false);
      setUsers((prev) => prev.filter((u: any) => u.id !== id));
    });
  };

  const handleEdit = (item: IUser) => {
    if (!canUpdate) return;
    setSelectedVal(item);
    setIsModalEditOpen(true);
  };

  const handleDelete = (item: IUser) => {
    if (!canDelete) return;
    setSelectedVal(item);
    setIsModalDeleteOpen(true);
  };

  const handleActivate = (item: IUser) => {
    if (!canExecute) return;
    setSelectedVal(item);
    setActivationCode('');
    setActivationLoading(true);

    axiosInstance
      .post(ApiConstants.User.sendActivationOtp, { userId: item.id })
      .then(() => {
        setIsActivationModalOpen(true);
      })
      .finally(() => setActivationLoading(false));
  };

  const confirmActivation = () => {
    if (!canExecute) return;
    if (!selectedVal?.id || activationCode.trim().length !== 6) {
      return;
    }

    setActivationLoading(true);
    axiosInstance
      .post(ApiConstants.User.activate, {
        userId: selectedVal.id,
        otpCode: activationCode.trim(),
      })
      .then(() => {
        setIsActivationModalOpen(false);
        setActivationCode('');
        getTable();
      })
      .finally(() => setActivationLoading(false));
  };

  useEffect(() => {
    getTable();
    getRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BasicCli>
      <div className="udWrap bcPageContent">
        <div className="udContainer">
          <div className="bcPageHeader">
            <h1 className="bcPageTitle">{i18n.t('menu.Users', { defaultValue: 'Users' })}</h1>
            <div className="bcPageHeaderActions">
              {canCreate && <button className="ui-control ui-btn ui-btn--primary" onClick={() => setIsModalOpen(true)} type="button">
                <PersonAddAltIcon fontSize="small" />
                {i18n.t('users.addUser')}
              </button>}
            </div>
          </div>

          {/* Table */}
          <div className=" udTable">
            <GeneralTable
              data={users}
              columns={gridItems}
              gridEntity={8}
              title=""
              hasRp={true}
              selectedItem={(val) => setSelectedVal(val)}
              onEdit={handleEdit}
              onDelete={handleDelete}
              canEdit={() => canUpdate}
              canDelete={() => canDelete}
              pagination={pagination}
              selectedItemPagination={(val) => selectedItemPagination(val)}
              extraActions={[
                {
                  key: 'activate',
                  title: 'Activate',
                  icon: <MarkEmailReadIcon fontSize="small" />,
                  onClick: handleActivate,
                  show: (item) => canExecute && !Boolean(item?.isActive),
                },
              ]}
              onFilterClose={(nextGridItems) => {
                setGridItems(nextGridItems);
                getTable(undefined, nextGridItems);
              }}
            />
          </div>

          {/* Edit */}
          {isModalEditOpen && (
            <EditModal
              title={i18n.t('users.editUser')}
              width="820px"
              closeModal={() => setIsModalEditOpen(false)}
              onSubmitData={() => setApprovedUser(true)}
              setIsModalOpen={setIsModalEditOpen}
              modalType={eFormType.Edit}
            >
              <UsersInventory
                roles={roles}
                createOrUpdate={approvedUser}
                tempUser={selectedVal}
                modalType={eFormType.Edit}
                onSubmitData={() => {
                  setApprovedUser(false);
                  getTable();
                }}
              />
            </EditModal>
          )}

          {/* Delete */}
          {isModalDeleteOpen && (
            <DeleteModal width="500px" handleClose={() => setIsModalDeleteOpen(false)} handleDelete={() => deleteDataSet(selectedVal?.id ?? 0)}>
              {i18n.t('users.areYouSureYouWantToDelete?')} <strong className="text-danger"> {selectedVal?.id}</strong>。
            </DeleteModal>
          )}

          {/* New */}
          {isModalOpen && (
            <EditModal
              title={i18n.t('users.addUser')}
              width="820px"
              closeModal={() => setIsModalOpen(false)}
              onSubmitData={() => setApprovedUser(true)}
              setIsModalOpen={setIsModalOpen}
              modalType={eFormType.New}
            >
              <UsersInventory
                createOrUpdate={approvedUser}
                roles={roles}
                modalType={eFormType.New}
                onSubmitData={() => {
                  setApprovedUser(false);
                  getTable();
                  setIsModalOpen(false);
                }}
              />
            </EditModal>
          )}

          {isActivationModalOpen && (
            <GeneralModal
              headerName="Activate User"
              handleClose={() => setIsActivationModalOpen(false)}
              handleClick={confirmActivation}
              width="520px"
              radius="14px"
            >
              <p style={{ marginBottom: 12 }}>Enter the 6-digit OTP code sent to {selectedVal?.email}.</p>
              <input
                className="form-control"
                type="text"
                maxLength={6}
                value={activationCode}
                onChange={(e) => setActivationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                disabled={activationLoading}
              />
            </GeneralModal>
          )}
        </div>
      </div>
    </BasicCli>
  );
};

export default UsersDetailsGrid;
